/**
 * Shared screenshot capture (openspec/feedback-redesign-unified.md §3.4, §6).
 *
 * One base64 reader for both feedback channels — the inline thumbs-down panel
 * (`AnswerActions`) and the product-feedback modal (`FeedbackModal`) — so capture
 * limits, accepted types, and the wire shape stay identical. The `data` /
 * `mimeType` pair is exactly what `/api/feedback` and `/api/product-feedback`
 * accept; `dataUrl` is the inline preview source.
 */

/** Accepted screenshot mime types (kept in sync with the server Zod enums). */
export const ACCEPTED_IMAGE = /^image\/(png|jpeg|jpg|gif|webp)$/i;
/** Raw byte cap (~3 MB) before base64 expansion. */
export const MAX_IMAGE_BYTES = 3_000_000;

export interface ReadImage {
  /** `data:` URL for an inline <img> preview. */
  dataUrl: string;
  /** Raw base64 (no `data:` prefix) — the wire payload. */
  data: string;
  mimeType: string;
}

/**
 * Read a File/Blob into a base64 screenshot. Resolves `null` when the file is the
 * wrong type, too large, or unreadable — never throws, so callers can surface a
 * friendly hint without a try/catch.
 */
export function readImageFile(file: File | Blob): Promise<ReadImage | null> {
  return new Promise((resolve) => {
    if (!ACCEPTED_IMAGE.test(file.type) || file.size > MAX_IMAGE_BYTES) {
      resolve(null);
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => resolve(null);
    reader.onload = () => {
      const dataUrl = String(reader.result);
      const comma = dataUrl.indexOf(",");
      if (comma < 0) {
        resolve(null);
        return;
      }
      resolve({ dataUrl, data: dataUrl.slice(comma + 1), mimeType: file.type });
    };
    reader.readAsDataURL(file);
  });
}
